import {type ChartAggOption} from '../../../generated/graphql';
import {
  BIN_POINT_TO_USE,
  type ConfidenceLevel,
} from './../../../util/plotHelpers/chartTypes';
import {
  type HistoryAggKey,
  type HistoryFallThroughKey,
  type HistoryRecord,
  type Point,
  type PointType,
  type ValidHistoryKey,
} from './types';

export type XKeyPositions = 'min' | 'avg' | 'last' | 'max';

const XKeymap: Record<XKeyPositions, PointType> = {
  avg: 'Avg',
  last: 'Last',
  max: 'Max',
  min: 'Min',
};

/**
 * This function returns the correct x-axis key for a given metric. It maps a metric to the value in the HistoryPoint with the correct suffix.
 *
 * If the y-axis metric is a system metric then the x-axis metric will be returned unmodified.
 */
export function getSafeXKey({
  xMetricKey,
  position = 'last',
  record,
}: {
  xMetricKey: string;
  record: HistoryRecord;
  position?: XKeyPositions;
}): ValidHistoryKey {
  const ffKey = `${xMetricKey}${XKeymap[position]}`;
  if (ffKey in record) {
    return ffKey as ValidHistoryKey;
  }
  return xMetricKey as ValidHistoryKey;
}
/**
 * This map exists to translate groupAgg options into suffixes that can be used to read data out of the api returned bins.
 *
 * A bin returns `-Min, -Max, -Last, and -Avg suffixed values for each metric. This translates the groupAgg option into the correct suffix so that the line construction logic automatically pulls the correct option.
 *
 * e.g. if you're constructing a group and you want to show all the max values you'd choose the max groupAgg option, and then when the buckets are reduced into the line during the grouping function the max value would be returned instead of the average or min value.
 */
// Columnar analog of hasMetric: does any bucketed {metric}{suffix} column exist?
export function columnsHaveMetric(
  cols: Record<string, unknown>,
  metric: string
): boolean {
  return (
    `${metric}Last` in cols ||
    `${metric}Min` in cols ||
    `${metric}Max` in cols ||
    `${metric}Avg` in cols
  );
}

export const mapGroupAggToSuffix: Record<ChartAggOption, PointType> = {
  mean: 'Avg',
  min: 'Min',
  max: 'Max',
  last: 'Last', //
  // these options aren't supported yet and won't be called
  // they're just here to satisfy the types for now
  median: 'Avg',
  sum: 'Avg',
  samples: 'Avg',
};

/**
 * If we're plotting a system metric plot (only usage currently known) on the y-axis we need to avoid adding the `Avg` suffix to the key because the API doesn't return these values in binned mode. This function takes in the yMetric (and an optional xMetric if needing to safely access that as well and returns prefixed or unprefixed keys as needed.)
 */
export function getSafeYKey({
  yMetricKey,
  overrides,
  aggType = BIN_POINT_TO_USE.toLowerCase() as ChartAggOption,
  record,
}: {
  yMetricKey: string;
  overrides?: {
    yAxisSuffix?: string;
  };
  aggType?: ChartAggOption;
  record: HistoryRecord;
}): ValidHistoryKey {
  // if we're doing a system metric plot we get results back that aren't binned
  // we can handle that by avoid transforming logic to use the `-Avg` suffix
  const suffix = mapGroupAggToSuffix[aggType] ?? BIN_POINT_TO_USE;
  const ySuffix = overrides?.yAxisSuffix ?? suffix;
  const ffKey = `${yMetricKey}${ySuffix}`;

  if (record && ffKey in record) {
    return ffKey as ValidHistoryKey;
  }

  return yMetricKey as ValidHistoryKey;
}

export function sortPointsByX(a: Point, b: Point) {
  return a.x - b.x;
}
export function takeLastDuplicate(point: Point, index: number, array: Point[]) {
  return index === array.length - 1 || array[index + 1].x !== point.x;
}

export function getAreaSafeYKeys(
  yMetricKey: string,
  record: HistoryRecord
): {
  minKey: HistoryAggKey | HistoryFallThroughKey;
  maxKey: HistoryAggKey | HistoryFallThroughKey;
  avgKey: HistoryAggKey | HistoryFallThroughKey;
  stdDevKey: ValidHistoryKey;
  stdErrKey: ValidHistoryKey;
  sumSqDevKey: ValidHistoryKey;
  countKey: ValidHistoryKey;
} {
  const useDefault = !recordHasKey(record, yMetricKey);
  const yMinSuffix = useDefault ? '' : 'Min';
  const yMaxSuffix = useDefault ? '' : 'Max';
  const yAvgSuffix = useDefault ? '' : 'Avg';
  const yStdDevSuffix = useDefault ? '' : 'StdDev';
  const yStdErrSuffix = useDefault ? '' : 'StdErr';
  const ySumSqDevSuffix = useDefault ? '' : 'SumSqDev';
  const yCountSuffix = useDefault ? '' : 'Count';
  return {
    minKey: `${yMetricKey}${yMinSuffix}` as
      | HistoryAggKey
      | HistoryFallThroughKey,
    maxKey: `${yMetricKey}${yMaxSuffix}` as
      | HistoryAggKey
      | HistoryFallThroughKey,
    avgKey: `${yMetricKey}${yAvgSuffix}` as
      | HistoryAggKey
      | HistoryFallThroughKey,
    stdDevKey: `${yMetricKey}${yStdDevSuffix}` as ValidHistoryKey,
    stdErrKey: `${yMetricKey}${yStdErrSuffix}` as ValidHistoryKey,
    sumSqDevKey: `${yMetricKey}${ySumSqDevSuffix}` as ValidHistoryKey,
    countKey: `${yMetricKey}${yCountSuffix}` as ValidHistoryKey,
  };
}

export function sampleStdDevFromSumSqDev(
  record: HistoryRecord,
  sumSqDevKey: ValidHistoryKey,
  countKey: ValidHistoryKey
): number {
  const n = Number(record[countKey]);
  const sumSqDev = Number(record[sumSqDevKey]);
  if (!Number.isFinite(n) || n < 2 || !Number.isFinite(sumSqDev)) {
    return NaN;
  }
  // A negative value is rounding error, not a real spread.
  return Math.sqrt(Math.max(sumSqDev, 0) / (n - 1));
}

export function sampleStdErrFromSumSqDev(
  record: HistoryRecord,
  sumSqDevKey: ValidHistoryKey,
  countKey: ValidHistoryKey
): number {
  const n = Number(record[countKey]);
  const sd = sampleStdDevFromSumSqDev(record, sumSqDevKey, countKey);
  if (!Number.isFinite(sd) || !Number.isFinite(n) || n < 2) {
    return NaN;
  }
  return sd / Math.sqrt(n);
}

/**
 * Two-sided Student t critical values, indexed by degrees of freedom
 * (df = Count - 1, so entry 0 is df = 1). A confidence interval is
 * mean ± t·SE. With few points the estimated σ is itself uncertain, so t is
 * wider than the normal z; it approaches z as df grows (95%: 12.706 at
 * df = 1, 2.042 at df = 30, 1.960 in the limit).
 *
 * These are the standard values printed in published t tables, rounded to
 * 3 decimals. Only three levels are ever needed, so a table avoids a
 * statistics dependency. Above df = 30, tCritical approximates t instead.
 */
const T_CRITICAL: Record<ConfidenceLevel, readonly number[]> = {
  90: [
    6.314, 2.92, 2.353, 2.132, 2.015, 1.943, 1.895, 1.86, 1.833, 1.812, 1.796,
    1.782, 1.771, 1.761, 1.753, 1.746, 1.74, 1.734, 1.729, 1.725, 1.721, 1.717,
    1.714, 1.711, 1.708, 1.706, 1.703, 1.701, 1.699, 1.697,
  ],
  95: [
    12.706, 4.303, 3.182, 2.776, 2.571, 2.447, 2.365, 2.306, 2.262, 2.228,
    2.201, 2.179, 2.16, 2.145, 2.131, 2.12, 2.11, 2.101, 2.093, 2.086, 2.08,
    2.074, 2.069, 2.064, 2.06, 2.056, 2.052, 2.048, 2.045, 2.042,
  ],
  99: [
    63.657, 9.925, 5.841, 4.604, 4.032, 3.707, 3.499, 3.355, 3.25, 3.169, 3.106,
    3.055, 3.012, 2.977, 2.947, 2.921, 2.898, 2.878, 2.861, 2.845, 2.831, 2.819,
    2.807, 2.797, 2.787, 2.779, 2.771, 2.763, 2.756, 2.75,
  ],
};

/**
 * Two-sided standard normal critical values: the central 90/95/99% of a
 * normal distribution lies within ±z. This is the limit of t as df grows. It
 * seeds the Cornish-Fisher approximation in tCritical, and confidenceMultiplier
 * uses it directly when a bin has no Count.
 */
const NORMAL_Z: Record<ConfidenceLevel, number> = {
  90: 1.644854,
  95: 1.959964,
  99: 2.575829,
};

function tCritical(df: number, level: ConfidenceLevel): number {
  if (df >= 1 && df <= 30) {
    return T_CRITICAL[level][df - 1];
  }
  // Cornish-Fisher expansion of the t quantile around z, through the 1/df³
  // term. For df above 30 its error is below 2e-5, well under a pixel.
  const z = NORMAL_Z[level];
  const z3 = z ** 3;
  const z5 = z ** 5;
  const z7 = z ** 7;
  return (
    z +
    (z3 + z) / (4 * df) +
    (5 * z5 + 16 * z3 + 3 * z) / (96 * df ** 2) +
    (3 * z7 + 19 * z5 + 17 * z3 - 15 * z) / (384 * df ** 3)
  );
}

/**
 * Multiplier that turns a standard error into a two-sided confidence
 * half-width. Uses Student t on `count - 1` degrees of freedom. When the count
 * is unknown (a grouped bin carries a finished StdErr key but no Count), falls
 * back to the normal z value.
 */
export function confidenceMultiplier(
  count: number,
  level: ConfidenceLevel
): number {
  if (!Number.isFinite(count) || count < 2) {
    return NORMAL_Z[level];
  }
  return tCritical(count - 1, level);
}

export function sliceObjectKeys<T extends Record<string, any>>(
  obj: T,
  numKeys: number
) {
  return Object.entries(obj).reduce((acc, [key, val], index) => {
    if (index < numKeys) {
      acc[key as keyof T] = val;
    }
    return acc;
  }, {} as Partial<T>);
}

export function recordHasKey(record: HistoryRecord, key: string) {
  return (
    `${key}Last` in record ||
    `${key}Min` in record ||
    `${key}Max` in record ||
    `${key}Avg` in record ||
    `${key}StdDev` in record ||
    `${key}StdErr` in record
  );
}
